import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSessionLaunch } from '../lib/useSessionLaunch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  LayoutDashboard, FileSpreadsheet, Settings, LogOut, ChevronDown,
  Calculator, Shield, Lock
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { usePageMeta } from '../hooks/usePageMeta';
import { toast } from 'sonner';

// Import components
import BankFileComparison from '../components/payroll/BankFileComparison';

const PayrollUtilitiesApp = () => {
  const [searchParams] = useSearchParams();
  
  // Set page title and favicon
  usePageMeta('Payroll Utilities | Harmony Apps', '/favicon-harmony.svg');
  
  // Get active tab from URL params
  const tabFromUrl = searchParams.get('tab') || 'dashboard';
  const [activeTab, setActiveTab] = useState(tabFromUrl);
  
  // Signed in from the visitor's own session (see useSessionLaunch); the role
  // checked below comes from that session, not from the URL.
  const { username, role } = useSessionLaunch();

  // Check if user has HR Admin role
  const isHRAdmin = role === 'hr_admin' || role === 'super_admin';

  const handleLogout = () => {
    window.close();
  };

  // If not HR Admin, show access denied
  if (!isHRAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <Shield className="w-8 h-8 text-red-600" />
            </div>
            <CardTitle className="text-red-600">Access Denied</CardTitle>
            <CardDescription>
              Only HR Admin users can access Payroll Utilities.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-sm text-slate-500 mb-4">
              Your current role: <Badge variant="outline">{role}</Badge>
            </p>
            <Button onClick={handleLogout} variant="outline">
              Close Window
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo & Title */}
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg">
                <Calculator className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-800">Payroll Utilities</h1>
                <p className="text-xs text-slate-500">Secure Payroll Tools</p>
              </div>
            </div>

            {/* Tabs */}
            <div className="hidden md:flex">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="bg-transparent gap-1">
                  <TabsTrigger 
                    value="dashboard" 
                    className="gap-2 data-[state=active]:bg-emerald-100 data-[state=active]:text-emerald-700"
                    data-testid="tab-dashboard"
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    Dashboard
                  </TabsTrigger>
                  <TabsTrigger 
                    value="bank-comparison" 
                    className="gap-2 data-[state=active]:bg-emerald-100 data-[state=active]:text-emerald-700"
                    data-testid="tab-bank-comparison"
                  >
                    <FileSpreadsheet className="w-4 h-4" />
                    Bank File Comparison
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                    <span className="text-sm font-medium text-emerald-700">
                      {username.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span className="hidden sm:inline">{username}</span>
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{username}</p>
                  <p className="text-xs text-muted-foreground capitalize">{role.replace('_', ' ')}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="w-4 h-4 mr-2" />
                  Close App
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Mobile Tabs */}
        <div className="md:hidden pb-4 -mx-4 px-4 overflow-x-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-transparent gap-1 w-max">
              <TabsTrigger value="dashboard" className="text-xs px-3">
                <LayoutDashboard className="w-3 h-3 mr-1" />
                Dashboard
              </TabsTrigger>
              <TabsTrigger value="bank-comparison" className="text-xs px-3">
                <FileSpreadsheet className="w-3 h-3 mr-1" />
                Bank Comparison
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Security Notice */}
        <div className="mb-6 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-3">
          <Lock className="w-5 h-5 text-emerald-600 flex-shrink-0" />
          <p className="text-sm text-emerald-700">
            <strong>Security:</strong> All payroll data is processed in-memory only and is never stored in the database. 
            Data is automatically cleared when you close this window or navigate away.
          </p>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'dashboard' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-slate-800 mb-2">Welcome to Payroll Utilities</h2>
                <p className="text-slate-600">Secure tools for payroll management and validation.</p>
              </div>

              {/* Utilities Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Card 
                  className="cursor-pointer hover:shadow-lg transition-shadow border-2 hover:border-emerald-300"
                  onClick={() => setActiveTab('bank-comparison')}
                >
                  <CardHeader>
                    <div className="p-3 bg-emerald-100 rounded-lg w-fit">
                      <FileSpreadsheet className="w-6 h-6 text-emerald-600" />
                    </div>
                    <CardTitle className="mt-4">Bank File Comparison</CardTitle>
                    <CardDescription>
                      Compare last month and current month bank files to identify new employees, 
                      missing employees, salary changes, and account number changes.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Badge className="bg-emerald-100 text-emerald-700">Available</Badge>
                  </CardContent>
                </Card>

                {/* Placeholder for future utilities */}
                <Card className="opacity-60">
                  <CardHeader>
                    <div className="p-3 bg-slate-100 rounded-lg w-fit">
                      <Calculator className="w-6 h-6 text-slate-400" />
                    </div>
                    <CardTitle className="mt-4 text-slate-400">Salary Calculator</CardTitle>
                    <CardDescription className="text-slate-400">
                      Calculate net salary, deductions, and tax based on CTC components.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Badge variant="outline">Coming Soon</Badge>
                  </CardContent>
                </Card>

                <Card className="opacity-60">
                  <CardHeader>
                    <div className="p-3 bg-slate-100 rounded-lg w-fit">
                      <Settings className="w-6 h-6 text-slate-400" />
                    </div>
                    <CardTitle className="mt-4 text-slate-400">Payslip Generator</CardTitle>
                    <CardDescription className="text-slate-400">
                      Generate and download individual or bulk payslips for employees.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Badge variant="outline">Coming Soon</Badge>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {activeTab === 'bank-comparison' && <BankFileComparison />}
        </div>
      </main>
    </div>
  );
};

export default PayrollUtilitiesApp;
